import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, message, Spin, Typography, Row, Col } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthService } from '../../services/authService';
import { LoginRequest } from '../../../../shared/types';
import './LoginPage.css';

const { Title, Text } = Typography;

interface LoginFormData {
  username: string;
  password: string;
}

const LoginPage: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Check if already logged in
  useEffect(() => {
    if (AuthService.isAuthenticated()) {
      navigate('/dashboard');
    }
  }, [navigate]);

  const handleSubmit = async (values: LoginFormData): Promise<void> => {
    try {
      setLoading(true);

      const loginData: LoginRequest = {
        username: values.username.trim(),
        password: values.password
      };

      const result = await AuthService.login(loginData);

      console.log('🔐 Login result received in component:', result);

      if (result.success) {
        message.success(result.message || 'Đăng nhập thành công!');
        
        // Debug: Check authentication status
        console.log('🔍 After login - isAuthenticated:', AuthService.isAuthenticated());
        console.log('🔍 After login - getToken:', AuthService.getToken());
        console.log('🔍 After login - getUser:', AuthService.getUser());
        
        // Small delay to ensure localStorage is updated
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Debug: Check again after delay
        console.log('🔍 After delay - isAuthenticated:', AuthService.isAuthenticated());
        console.log('🔍 After delay - getToken:', AuthService.getToken());
        console.log('🔍 After delay - getUser:', AuthService.getUser());
        
        // Use navigate instead of window.location.href for debugging
        const redirectPath = new URLSearchParams(location.search).get('redirect') || '/dashboard';
        console.log('🔍 About to redirect to:', redirectPath);
        
        // Temporarily comment out redirect for debugging
        // window.location.href = redirectPath;
        navigate(redirectPath);
      } else {
        console.log('❌ Login failed in component:', result.message);
        message.error(result.message || 'Đăng nhập thất bại');
      }
    } catch (error) {
      console.error('Login error:', error);
      message.error('Có lỗi xảy ra khi đăng nhập');
    } finally {
      setLoading(false);
    }
  };

  const handleFormFailed = (errorInfo: any): void => {
    console.log('Failed:', errorInfo);
    message.error('Vui lòng kiểm tra lại thông tin đăng nhập');
  };

  const handleLogoError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    // Fallback to placeholder if logo fails to load
    e.currentTarget.style.display = 'none';
    const placeholder = document.createElement('div');
    placeholder.className = 'logo-placeholder';
    placeholder.style.cssText = `
      width: 64px; 
      height: 64px; 
      background: linear-gradient(135deg, #1890ff, #096dd9);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 16px;
      color: white;
      font-size: 24px;
      font-weight: bold;
    `;
    placeholder.textContent = '🏫';
    e.currentTarget.parentNode?.appendChild(placeholder);
  };

  return (
    <div className="login-container">
      {/* Fullscreen background with image and blur overlay */}
      <div className="login-bg-image" />
      <div className="login-overlay" />

      <div className="login-content">
        <Row gutter={[48, 24]} align="middle" className="login-row">
          {/* Left illustrative area */}
          <Col xs={0} md={14} className="login-illustration">
            <div className="illus-title">Hệ thống Quản trị</div>
            <div className="illus-panels"></div>
          </Col>

          {/* Right login card */}
          <Col xs={24} md={10} className="login-right">
            <Card className="login-card">
              <div className="login-header">
                <div className="logo-container">
                  <img
                    src="/logoCDYTB.png"
                    alt="Logo Cao Đẳng Y Tế Thái Bình"
                    className="login-logo"
                    onError={handleLogoError}
                  />
                </div>
                <Title level={3} className="login-title">Cao Đẳng Y Tế Thái Bình</Title>
                <Text type="secondary" className="login-subtitle">Hệ thống Quản lý Trường học - Đăng nhập để truy cập</Text>
              </div>

              <Form
                form={form}
                name="login"
                onFinish={handleSubmit}
                onFinishFailed={handleFormFailed}
                autoComplete="off"
                layout="vertical"
                size="large"
              >
                <Form.Item name="username" rules={[{ required: true, message: 'Vui lòng nhập tên đăng nhập!' }, { min: 3, message: 'Tên đăng nhập phải có ít nhất 3 ký tự!' }]}
                >
                  <Input
                    prefix={<UserOutlined />}
                    placeholder="Tên đăng nhập"
                    disabled={loading}
                    autoFocus
                  />
                </Form.Item>

                <Form.Item name="password" rules={[{ required: true, message: 'Vui lòng nhập mật khẩu!' }, { min: 6, message: 'Mật khẩu phải có ít nhất 6 ký tự!' }]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="Mật khẩu"
                    disabled={loading}
                    onPressEnter={() => form.submit()}
                  />
                </Form.Item>

                <Form.Item style={{ marginBottom: 8 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    block
                    size="large"
                  >
                    {loading ? (
                      <>
                        <Spin size="small" style={{ marginRight: 8 }} />
                        Đang đăng nhập...
                      </>
                    ) : 'Đăng nhập'}
                  </Button>
                </Form.Item>
              </Form>

              <div className="login-footer">
                <Text type="secondary" style={{ fontSize: 12 }}>
                  © 2024 Cao Đẳng Y Tế Thái Bình v1.0.0
                </Text>
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default LoginPage;